import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Tree, TreeSelect } from "antd";
import { useQuery } from "@apollo/client";
import { GET_LEAD_SOURCES_DS } from "../../graphql/query";
import { UserContext } from "../../amplify/authenticator-provider";
import DebounceInput from "./DebounceInput";

/**
 * Reusable multi-select for opportunity sources. Self-fetches the list via
 * `GET_OPPORTUNITY_SOURCES` (company + current-location scoped) and renders a
 * hierarchical picker.
 *
 * Variants:
 *   - `variant="select"` (default): AntD `TreeSelect` — a compact input that
 *     opens a hierarchical dropdown on focus. Best for filter bars where the
 *     picker sits next to other inputs.
 *   - `variant="inline"`: renders a plain Search input + checkable AntD
 *     `Tree` inline — no popup, no portal. Best when the caller already
 *     provides the container (e.g. an AntD Table `filterDropdown`), where a
 *     nested popup would collide with the parent's own open/close lifecycle.
 *
 * Modes:
 *   - `showApplyButton={false}` (default): every checkbox toggle emits
 *     `onChange(ids)` immediately, so the caller refetches as the user picks.
 *   - `showApplyButton={true}`: selections accumulate in an internal draft
 *     and are only committed when the user clicks Apply — useful when the
 *     downstream query is expensive.
 *
 * The footer always renders a Reset button that clears everything (and emits
 * `onChange([])` regardless of mode).
 *
 * @param {object}   props
 * @param {number[]} [props.value]            Committed selected source IDs.
 * @param {(ids:number[])=>void} props.onChange
 * @param {"select"|"inline"} [props.variant="select"]
 * @param {boolean}  [props.showSearch=true]  Toggle the search box.
 * @param {boolean}  [props.showApplyButton=false] Defer commits to an Apply click.
 * @param {boolean}  [props.allowClear=true]  Show the X clear icon on the
 *                                             `select` variant's input.
 * @param {string}   [props.placeholder]
 * @param {string}   [props.className]
 */
const OpportunitySourceSelect = ({
  value = [],
  onChange,
  variant = "select",
  showSearch = true,
  showApplyButton = false,
  allowClear = true,
  placeholder = "Select opportunity sources",
  className = "w-full",
  // Called by Apply and Reset after they commit — lets an outer
  // container (e.g. an AntD Table `filterDropdown`) close its own panel
  // in response. No-op for standalone usage.
  onClose,
}) => {
  const { user } = useContext(UserContext);
  const companyId = user?.company?.id ? +user.company.id : null;

  // Mirrors the helper used elsewhere in the codebase (leadCenter, prospect):
  // sessionStorage holds either a single id or comma-separated ids.
  const locationIds = useMemo(() => {
    const raw = sessionStorage.getItem("currentLocationId") || "";
    return raw
      .split(",")
      .filter(Boolean)
      .map(id => +id);
  }, []);

  const { data, loading } = useQuery(GET_LEAD_SOURCES_DS, {
    variables: { companyId, locationIds },
    skip: !companyId || locationIds.length === 0,
    fetchPolicy: "cache-first",
  });

  // `getLeadSources` is already hierarchical (3 levels): each top node has a
  // `parents[]` array (mid-level), and each parent has a `childs[]` array
  // (leaves). Map directly into the tree shape (`title/value/key/children`)
  // shared by both TreeSelect and Tree.
  const treeData = useMemo(() => {
    const roots = data?.getLeadSources || [];
    return roots.map(root => ({
      title: root.name,
      value: +root.key,
      key: +root.key,
      children: (root.parents || []).map(p => ({
        title: p.name,
        value: +p.key,
        key: +p.key,
        children: (p.childs || []).map(c => ({
          title: c.name,
          value: +c.key,
          key: +c.key,
        })),
      })),
    }));
  }, [data]);

  // Draft mirrors the upstream `value` until the user toggles checkboxes.
  // In apply mode it stays detached until Apply; in immediate mode it just
  // shadows value (onChange fires on every toggle, value comes right back).
  const [draft, setDraft] = useState(value);
  // Track open state so we can reset the draft each time the dropdown opens
  // (apply mode): unapplied selections shouldn't survive a close+reopen.
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const commitChange = next => {
    setDraft(next);
    if (!showApplyButton) onChange?.(next);
  };

  const handleApply = () => {
    onChange?.(draft);
    setOpen(false);
    onClose?.();
  };

  const handleReset = () => {
    setDraft([]);
    onChange?.([]);
    setOpen(false);
    onClose?.();
  };

  // Allow-clear X icon handler — immediately commits the clear so the caller
  // refetches right away, even in apply mode.
  const handleClear = () => {
    setDraft([]);
    onChange?.([]);
  };

  const handleDropdownVisibleChange = nextOpen => {
    setOpen(nextOpen);
    if (nextOpen && showApplyButton) {
      // Re-sync draft on open so a previous unapplied state can't leak in.
      setDraft(value);
    }
  };

  const draftChanged = JSON.stringify(draft) !== JSON.stringify(value);

  const footer = (
    <div className="flex justify-end gap-2 p-2 border-t border-gray-200">
      <Button size="small" onClick={handleReset} disabled={loading || draft.length === 0}>
        Reset
      </Button>
      {showApplyButton && (
        <Button
          size="small"
          type="primary"
          onClick={handleApply}
          disabled={loading || !draftChanged}
        >
          Apply
        </Button>
      )}
    </div>
  );

  // ── Inline variant ──────────────────────────────────────────────────────
  // Plain search + checkable Tree, no popup. Callers own the container
  // (e.g. AntD Table `filterDropdown`).
  if (variant === "inline") {
    return (
      <InlineTreeSourcePicker
        treeData={treeData}
        draft={draft}
        onCheck={commitChange}
        footer={footer}
        loading={loading}
        showSearch={showSearch}
        className={className}
      />
    );
  }

  // ── Select variant (default) ────────────────────────────────────────────
  const dropdownRender = menu => (
    <>
      {menu}
      {footer}
    </>
  );

  // AntD resolves a chip's label by looking the value up in `treeData`. When
  // we land here from a dashboard drill-in, `value` is already populated from
  // redux but the sources query is still in flight, so AntD caches the raw
  // numeric id as the label and never re-resolves once the tree finally
  // arrives. Re-mount the TreeSelect once data lands so the labels resolve
  // against the loaded tree. Cheap because `cache-first` keeps the second
  // mount instant for any later visit.
  const treeReady = treeData.length > 0;

  return (
    <TreeSelect
      key={treeReady ? "loaded" : "empty"}
      treeData={treeData}
      value={draft}
      onChange={commitChange}
      open={open}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      placeholder={placeholder}
      treeCheckable
      // SHOW_ALL emits the parent's id together with every descendant id when
      // a parent is checked — the BE needs the explicit child list, it
      // doesn't expand a parent on its own. (SHOW_PARENT would have collapsed
      // the value to just the parent id, hiding the children.)
      showCheckedStrategy={TreeSelect.SHOW_ALL}
      showSearch={showSearch}
      treeNodeFilterProp="title"
      maxTagCount="responsive"
      allowClear={allowClear}
      onClear={handleClear}
      className={className}
      loading={loading}
      dropdownRender={dropdownRender}
    />
  );
};

// Extracted so the inline variant can own its own search + expand state
// without polluting the shared component's hook order.
const InlineTreeSourcePicker = ({
  treeData,
  draft,
  onCheck,
  footer,
  loading,
  showSearch,
  className,
}) => {
  const [search, setSearch] = useState("");
  // Controlled expansion — tracks whatever the user clicked. Kept
  // separate from the search-driven auto-expand so users can freely
  // collapse / expand parents while a search is active.
  const [expandedKeys, setExpandedKeys] = useState([]);

  // Any node whose title matches (case-insensitive) — and every one of
  // its ancestor keys so the matching leaf stays reachable in the tree.
  const { filteredTree, searchAncestorKeys } = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return { filteredTree: treeData, searchAncestorKeys: [] };

    const matchedAncestors = new Set();
    const filter = nodes =>
      (nodes || [])
        .map(n => {
          const kids = filter(n.children);
          const selfMatch = String(n.title || "")
            .toLowerCase()
            .includes(term);
          if (selfMatch || kids.length) {
            if (kids.length) matchedAncestors.add(n.key);
            return { ...n, children: kids.length ? kids : undefined };
          }
          return null;
        })
        .filter(Boolean);

    return { filteredTree: filter(treeData), searchAncestorKeys: [...matchedAncestors] };
  }, [treeData, search]);

  // When a search term produces matches, auto-expand every ancestor of a
  // match so the hit is visible. The user can still collapse them again;
  // `onExpand` overwrites `expandedKeys` fully.
  useEffect(() => {
    if (searchAncestorKeys.length) setExpandedKeys(searchAncestorKeys);
  }, [searchAncestorKeys]);

  return (
    <div className={className}>
      {showSearch && (
        <DebounceInput
          allowClear
          placeholder="Search sources"
          value={search}
          onChange={next => setSearch(next || "")}
          className="mb-2"
        />
      )}
      <div style={{ maxHeight: 260, overflowY: "auto" }}>
        {loading && !treeData.length ? (
          <div className="text-black-45 text-sm px-2 py-4 text-center">Loading…</div>
        ) : (
          <Tree
            checkable
            selectable={false}
            treeData={filteredTree}
            checkedKeys={draft}
            onCheck={keys => {
              // With `checkStrictly` unset (default), `keys` is the array of
              // checked leaf/branch keys — matches what the API expects.
              const next = Array.isArray(keys) ? keys : keys?.checked || [];
              onCheck(next.map(Number));
            }}
            expandedKeys={expandedKeys}
            onExpand={keys => setExpandedKeys(keys)}
          />
        )}
      </div>
      {footer}
    </div>
  );
};

export default OpportunitySourceSelect;
